import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Switch,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  VpnKey as KeyIcon,
  ContentCopy as CopyIcon,
  SwapHoriz as LinkIcon,
  Block as BlockIcon,
  Check as CheckIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { PlayerAccountRequest, PasswordResetRequest, Player } from "../types";
import { apiRequest } from "../config/api";

function PlayerAccountRequests() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [tab, setTab] = useState(0); // 0 = Account Requests, 1 = Password Reset Requests, 2 = Approved Accounts, 3 = Rejected Accounts, 4 = Restricted Players
  const [accountRequests, setAccountRequests] = useState<
    PlayerAccountRequest[]
  >([]);
  const [passwordResets, setPasswordResets] = useState<PasswordResetRequest[]>(
    [],
  );
  const [approvedAccounts, setApprovedAccounts] = useState<Player[]>([]);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [autoApproveEnabled, setAutoApproveEnabled] = useState(false);
  const [savingAutoApprove, setSavingAutoApprove] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    requestId: string;
    action: "approve" | "reject" | "process" | "delete" | "link";
    type: "account" | "password";
    displayText: string;
  }>({
    open: false,
    requestId: "",
    action: "approve",
    type: "account",
    displayText: "",
  });

  const [rejectionReason, setRejectionReason] = useState("");
  const [banDialog, setBanDialog] = useState<{
    open: boolean;
    player: Player | null;
  }>({
    open: false,
    player: null,
  });
  const [banReason, setBanReason] = useState("");
  const [banType, setBanType] = useState<'league-days' | 'tournaments' | 'both'>('both');

  const [editRestrictionDialog, setEditRestrictionDialog] = useState<{
    open: boolean;
    player: Player | null;
  }>({ open: false, player: null });
  const [editBanType, setEditBanType] = useState<'league-days' | 'tournaments' | 'both'>('both');
  const [editBanReason, setEditBanReason] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [newLinkedPlayerId, setNewLinkedPlayerId] = useState("");

  const [tempPasswordDialog, setTempPasswordDialog] = useState<{
    open: boolean;
    player: Player | null;
    generatedPassword: string | null;
    loading: boolean;
  }>({ open: false, player: null, generatedPassword: null, loading: false });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    fetchData();
    fetchAutoApproveSettings();
  }, []);

  const fetchAutoApproveSettings = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        "/api/player-requests/auto-approve-settings",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setAutoApproveEnabled(data.autoApproveAccounts || false);
      }
    } catch (error) {
      console.error("Error fetching auto-approve settings:", error);
    }
  };

  const handleAutoApproveToggle = async (enabled: boolean) => {
    setSavingAutoApprove(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        "/api/player-requests/auto-approve-settings",
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ autoApproveAccounts: enabled }),
        },
      );

      if (response.ok) {
        setAutoApproveEnabled(enabled);
        setSnackbar({
          open: true,
          message: enabled
            ? "Auto-approval enabled. New account requests will be automatically approved."
            : "Auto-approval disabled. Account requests will require manual approval.",
          severity: "success",
        });
      } else {
        throw new Error("Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating auto-approve settings:", error);
      setSnackbar({
        open: true,
        message: "Failed to update auto-approve settings",
        severity: "error",
      });
    } finally {
      setSavingAutoApprove(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchAccountRequests(),
      fetchPasswordResets(),
      fetchApprovedAccounts(),
      fetchAllPlayers(),
    ]);
    setLoading(false);
  };

  const fetchAccountRequests = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        "/api/player-requests/account-requests",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setAccountRequests(data);
      }
    } catch (error) {
      console.error("Error fetching account requests:", error);
    }
  };

  const fetchPasswordResets = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        "/api/player-requests/password-reset-requests",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setPasswordResets(data);
      }
    } catch (error) {
      console.error("Error fetching password reset requests:", error);
    }
  };

  const fetchApprovedAccounts = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        "/api/player-requests/approved-accounts",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Approved accounts data:", data);
        setApprovedAccounts(data);
      }
    } catch (error) {
      console.error("Error fetching approved accounts:", error);
    }
  };

  const fetchAllPlayers = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest("/api/players", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAllPlayers(data.filter((p: Player) => p.isActive));
      }
    } catch (error) {
      console.error("Error fetching all players:", error);
    }
  };

  const handleApproveAccount = (requestId: string, username: string) => {
    setConfirmDialog({
      open: true,
      requestId,
      action: "approve",
      type: "account",
      displayText: username,
    });
  };

  const handleRejectAccount = (requestId: string, username: string) => {
    setConfirmDialog({
      open: true,
      requestId,
      action: "reject",
      type: "account",
      displayText: username,
    });
    setRejectionReason("");
  };

  const handleProcessPasswordReset = (requestId: string, username: string) => {
    setConfirmDialog({
      open: true,
      requestId,
      action: "process",
      type: "password",
      displayText: username,
    });
  };

  const handleDeleteAccount = (playerId: string, playerName: string) => {
    setConfirmDialog({
      open: true,
      requestId: playerId,
      action: "delete",
      type: "account",
      displayText: playerName,
    });
  };

  const handleOpenTempPasswordDialog = (player: Player) => {
    setTempPasswordDialog({ open: true, player, generatedPassword: null, loading: false });
  };

  const handleGenerateTempPassword = async () => {
    if (!tempPasswordDialog.player) return;
    setTempPasswordDialog((prev) => ({ ...prev, loading: true }));
    try {
      const token = localStorage.getItem("authToken");
      const response = await apiRequest(
        `/api/player-requests/accounts/${tempPasswordDialog.player.id}/reset-password`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setTempPasswordDialog((prev) => ({
          ...prev,
          generatedPassword: data.temporaryPassword,
          loading: false,
        }));
      } else {
        const err = await response.json();
        setSnackbar({ open: true, message: err.error || "Failed to generate password", severity: "error" });
        setTempPasswordDialog((prev) => ({ ...prev, loading: false }));
      }
    } catch {
      setSnackbar({ open: true, message: "Failed to generate password", severity: "error" });
      setTempPasswordDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleLinkAccount = (playerId: string, playerName: string) => {
    setConfirmDialog({
      open: true,
      requestId: playerId,
      action: "link",
      type: "account",
      displayText: playerName,
    });
    setNewLinkedPlayerId("");
  };

  const handleDeletePasswordReset = (requestId: string, username: string) => {
    setConfirmDialog({
      open: true,
      requestId,
      action: "delete",
      type: "password",
      displayText: username,
    });
  };

  const confirmAction = async () => {
    const { requestId, action, type } = confirmDialog;

    try {
      setProcessing(requestId);
      const token = localStorage.getItem("authToken");
      let response;

      if (type === "account" && action === "approve") {
        response = await apiRequest(
          `/api/player-requests/account-requests/${requestId}/approve`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } else if (type === "account" && action === "reject") {
        response = await apiRequest(
          `/api/player-requests/account-requests/${requestId}/reject`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
            body: JSON.stringify({ reason: rejectionReason }),
          },
        );
      } else if (type === "account" && action === "delete") {
        response = await apiRequest(
          `/api/player-requests/accounts/${requestId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      } else if (type === "account" && action === "link") {
        if (!newLinkedPlayerId) {
          setSnackbar({
            open: true,
            message: "Please select a player profile to link to",
            severity: "error",
          });
          setProcessing(null);
          return;
        }
        response = await apiRequest(
          `/api/player-requests/accounts/${requestId}/link`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
            body: JSON.stringify({ newLinkedPlayerId }),
          },
        );
      } else if (type === "password" && action === "process") {
        response = await apiRequest(
          `/api/player-requests/password-reset-requests/${requestId}/process`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.ok) {
          const data = await response.json();
          setTempPassword(data.temporaryPassword);
        }
      } else if (type === "password" && action === "delete") {
        response = await apiRequest(
          `/api/player-requests/password-reset-requests/${requestId}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        );
      }

      if (response && response.ok) {
        setSnackbar({
          open: true,
          message: `Request ${action}d successfully!`,
          severity: "success",
        });
        await fetchData();

        // Don't close dialog immediately if temp password was generated
        if (!(type === "password" && action === "process")) {
          closeDialog();
        }
      } else {
        const errorData = await response?.json();
        setSnackbar({
          open: true,
          message: errorData?.error || `Failed to ${action} request`,
          severity: "error",
        });
      }
    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      setSnackbar({
        open: true,
        message: `Network error while ${action}ing request`,
        severity: "error",
      });
    } finally {
      setProcessing(null);
    }
  };

  const closeDialog = () => {
    setConfirmDialog({
      open: false,
      requestId: "",
      action: "approve",
      type: "account",
      displayText: "",
    });
    setRejectionReason("");
    setTempPassword("");
    setNewLinkedPlayerId("");
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: "Copied to clipboard!",
      severity: "success",
    });
  };

  const formatDate = (date: any, showNeverForNull: boolean = false) => {
    if (!date) return showNeverForNull ? "Never" : "N/A";
    try {
      // Handle Firestore Timestamp
      if (date.toDate) {
        return date.toDate().toLocaleDateString("en-GB", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      }
      // Handle ISO string or regular Date
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return showNeverForNull ? "Never" : "N/A";
      return dateObj.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error, date);
      return showNeverForNull ? "Never" : "N/A";
    }
  };

  const getStatusChip = (status: string) => {
    const colors: Record<string, "default" | "success" | "error" | "warning"> =
      {
        pending: "warning",
        approved: "success",
        rejected: "error",
        completed: "success",
        expired: "default",
      };

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={colors[status] || "default"}
        size="small"
      />
    );
  };

  const handleBanToggle = (player: Player) => {
    setBanDialog({ open: true, player });
    setBanReason(player.bannedReason || "");
    setBanType((player.banType as any) || 'both');
  };

  const handleBanConfirm = async () => {
    if (!banDialog.player) return;
    
    const token = localStorage.getItem("authToken");
    if (!token) {
      setSnackbar({ open: true, message: "Unauthorized", severity: "error" });
      return;
    }

    setProcessing(banDialog.player.id);
    
    try {
      const response = await apiRequest(
        `/api/players/${banDialog.player.id}/ban`,
        {
          method: "PUT",
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ 
            isBanned: !banDialog.player.isBanned,
            bannedReason: banReason,
            banType: !banDialog.player.isBanned ? banType : undefined,
          }),
        },
      );

      if (response.ok) {
        setSnackbar({
          open: true,
          message: banDialog.player.isBanned 
            ? "Player restriction removed successfully" 
            : "Player restricted successfully",
          severity: "success",
        });
        fetchData();
      } else {
        const errorData = await response.json();
        setSnackbar({
          open: true,
          message: errorData.error || "Failed to update ban status",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating ban status:", error);
      setSnackbar({
        open: true,
        message: "Failed to update ban status",
        severity: "error",
      });
    } finally {
      setProcessing(null);
      setBanDialog({ open: false, player: null });
      setBanReason("");
      setBanType('both');
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const pendingAccountRequests = accountRequests.filter(
    (r) => r.status === "pending",
  );
  const rejectedAccountRequests = accountRequests.filter(
    (r) => r.status === "rejected",
  );
  const pendingPasswordResets = passwordResets.filter(
    (r) => r.status === "pending",
  );
  const approvedAccountsCount = approvedAccounts.length;
  const restrictedPlayers = approvedAccounts.filter((a) => a.isBanned);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Player Account Management
        </Typography>
        
        <FormControlLabel
          control={
            <Switch
              checked={autoApproveEnabled}
              onChange={(e) => handleAutoApproveToggle(e.target.checked)}
              disabled={savingAutoApprove}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                Auto-Approve New Requests
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {autoApproveEnabled 
                  ? "New requests are automatically approved" 
                  : "Requests require manual approval"}
              </Typography>
            </Box>
          }
        />
      </Box>

      {(pendingAccountRequests.length > 0 ||
        pendingPasswordResets.length > 0) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {pendingAccountRequests.length} pending account request(s) and{" "}
          {pendingPasswordResets.length} pending password reset(s)
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(_, newValue) => setTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                minWidth: { xs: "auto", md: 120 },
                fontSize: { xs: "0.7rem", sm: "0.875rem" },
                px: { xs: 0.5, sm: 2 },
              },
            }}
          >
            <Tab 
              label={isMobile 
                ? `Requests (${pendingAccountRequests.length})` 
                : `Account Requests (${pendingAccountRequests.length})`
              } 
            />
            <Tab 
              label={isMobile 
                ? `Resets (${pendingPasswordResets.length})` 
                : `Password Resets (${pendingPasswordResets.length})`
              } 
            />
            <Tab 
              label={isMobile 
                ? `Approved (${approvedAccountsCount})` 
                : `Approved Accounts (${approvedAccountsCount})`
              } 
            />
            <Tab
              label={isMobile 
                ? `Rejected (${rejectedAccountRequests.length})` 
                : `Rejected Accounts (${rejectedAccountRequests.length})`
              }
            />
            <Tab
              label={isMobile
                ? `Restricted (${restrictedPlayers.length})`
                : `Restricted Players (${restrictedPlayers.length})`
              }
              sx={{ color: restrictedPlayers.length > 0 ? 'error.main' : undefined }}
            />
          </Tabs>
        </Box>

        <CardContent>
          {/* Account Requests Tab */}
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Division</TableCell>
                    <TableCell>Linked Player</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accountRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No account requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingAccountRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>{request.username}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            {request.name}
                            {(request as any).requestType === "tournament" && (
                              <Chip label="Tournament Account" size="small" color="info" sx={{ height: 20, fontSize: "0.7rem" }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{request.division}</TableCell>
                        <TableCell>{request.linkedPlayerName}</TableCell>
                        <TableCell>{formatDate(request.createdAt)}</TableCell>
                        <TableCell>{getStatusChip(request.status)}</TableCell>
                        <TableCell align="right">
                          {request.status === "pending" && (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                              }}
                            >
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<ApproveIcon />}
                                onClick={() =>
                                  handleApproveAccount(
                                    request.id,
                                    request.username,
                                  )
                                }
                                disabled={processing === request.id}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<RejectIcon />}
                                onClick={() =>
                                  handleRejectAccount(
                                    request.id,
                                    request.username,
                                  )
                                }
                                disabled={processing === request.id}
                              >
                                Reject
                              </Button>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Password Reset Requests Tab */}
          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Player Name</TableCell>
                    <TableCell>Mobile (WhatsApp)</TableCell>
                    <TableCell>Requested At</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Temp Password</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {passwordResets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No password reset requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    passwordResets.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>{request.username}</TableCell>
                        <TableCell>{request.playerName}</TableCell>
                        <TableCell>{request.mobileNumber || "N/A"}</TableCell>
                        <TableCell>{formatDate(request.createdAt)}</TableCell>
                        <TableCell>{getStatusChip(request.status)}</TableCell>
                        <TableCell>
                          {request.temporaryPassword && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <code>{request.temporaryPassword}</code>
                              <Tooltip title="Copy password">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    copyToClipboard(request.temporaryPassword!)
                                  }
                                >
                                  <CopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                              alignItems: "center",
                            }}
                          >
                            {request.status === "pending" && (
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<KeyIcon />}
                                onClick={() =>
                                  handleProcessPasswordReset(
                                    request.id,
                                    request.username,
                                  )
                                }
                                disabled={processing === request.id}
                              >
                                Generate Password
                              </Button>
                            )}
                            <Tooltip title="Delete request">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeletePasswordReset(
                                    request.id,
                                    request.username,
                                  )
                                }
                                disabled={processing === request.id}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Approved Accounts Tab */}
          {tab === 2 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Player Name</TableCell>
                    <TableCell>Division</TableCell>
                    <TableCell>Approval Type</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvedAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No approved accounts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    approvedAccounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.username}</TableCell>
                        <TableCell>{account.name}</TableCell>
                        <TableCell>{account.division}</TableCell>
                        <TableCell>
                          <Chip
                            label={(account as any).approvalType === "automatic" ? "Automatic" : "Manual"}
                            size="small"
                            color={(account as any).approvalType === "automatic" ? "info" : "default"}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          {formatDate(account.accountCreatedAt)}
                        </TableCell>
                        <TableCell>
                          {formatDate(account.lastLoginAt, true)}
                        </TableCell>
                        <TableCell>
                          {account.isBanned ? (
                            <Chip 
                              label={
                                account.banType === 'league-days' ? 'Restricted (League)' 
                                : account.banType === 'tournaments' ? 'Restricted (Tournaments)'
                                : 'Restricted (All)'
                              }
                              color="error" 
                              size="small"
                              title={account.bannedReason || "No reason provided"}
                            />
                          ) : (
                            <Chip label="Active" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            <Tooltip title="Generate temporary password">
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => handleOpenTempPasswordDialog(account)}
                                disabled={processing === account.id}
                              >
                                <KeyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={account.isBanned ? "Remove restriction" : "Restrict player"}>
                              <IconButton
                                size="small"
                                color={account.isBanned ? "success" : "warning"}
                                onClick={() => handleBanToggle(account)}
                                disabled={processing === account.id}
                              >
                                {account.isBanned ? <CheckIcon /> : <BlockIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Change linked player profile">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() =>
                                  handleLinkAccount(account.id, account.name)
                                }
                                disabled={processing === account.id}
                              >
                                <LinkIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete account">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleDeleteAccount(account.id, account.name)
                                }
                                disabled={processing === account.id}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Rejected Accounts Tab */}
          {tab === 3 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Division</TableCell>
                    <TableCell>Linked Player</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Rejected At</TableCell>
                    <TableCell>Rejection Reason</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rejectedAccountRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No rejected account requests found
                      </TableCell>
                    </TableRow>
                  ) : (
                    rejectedAccountRequests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell>{request.username}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                            {request.name}
                            {(request as any).requestType === "tournament" && (
                              <Chip label="Tournament Account" size="small" color="info" sx={{ height: 20, fontSize: "0.7rem" }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{request.division}</TableCell>
                        <TableCell>{request.linkedPlayerName}</TableCell>
                        <TableCell>{formatDate(request.createdAt)}</TableCell>
                        <TableCell>{formatDate(request.reviewedAt)}</TableCell>
                        <TableCell>
                          {request.rejectionReason || "No reason provided"}
                        </TableCell>
                        <TableCell>{getStatusChip(request.status)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Restricted Players Tab */}
          {tab === 4 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Player Name</TableCell>
                    <TableCell>Division</TableCell>
                    <TableCell>Restricted From</TableCell>
                    <TableCell>Restricted Since</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {restrictedPlayers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No restricted players
                      </TableCell>
                    </TableRow>
                  ) : (
                    restrictedPlayers.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell>{account.username}</TableCell>
                        <TableCell>{account.name}</TableCell>
                        <TableCell>{account.division}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              account.banType === 'league-days' ? 'League Days'
                              : account.banType === 'tournaments' ? 'Tournaments'
                              : 'League Days & Tournaments'
                            }
                            color="error"
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate((account as any).bannedAt)}</TableCell>
                        <TableCell>{account.bannedReason || "No reason provided"}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Edit restriction">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setEditRestrictionDialog({ open: true, player: account });
                                  setEditBanType((account.banType as any) || 'both');
                                  setEditBanReason(account.bannedReason || '');
                                }}
                                disabled={processing === account.id}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove restriction">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleBanToggle(account)}
                                disabled={processing === account.id}
                              >
                                {processing === account.id ? <CircularProgress size={18} /> : <CheckIcon />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === "approve" && "Approve Account Request"}
          {confirmDialog.action === "reject" && "Reject Account Request"}
          {confirmDialog.action === "process" && "Generate Temporary Password"}
          {confirmDialog.action === "delete" && confirmDialog.type === "account" && "Delete Player Account"}
          {confirmDialog.action === "delete" && confirmDialog.type === "password" && "Delete Password Reset Request"}
          {confirmDialog.action === "link" && "Change Linked Player Profile"}
        </DialogTitle>
        <DialogContent>
          {tempPassword ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Temporary password generated successfully!
              </Alert>
              <Typography variant="body2" gutterBottom>
                Share this temporary password with the player:
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  p: 2,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                  {tempPassword}
                </Typography>
                <IconButton onClick={() => copyToClipboard(tempPassword)}>
                  <CopyIcon />
                </IconButton>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                The player will be required to change this password upon first
                login.
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {confirmDialog.action === "approve" &&
                  `Are you sure you want to approve the account request for "${confirmDialog.displayText}"?`}
                {confirmDialog.action === "reject" &&
                  `Are you sure you want to reject the account request for "${confirmDialog.displayText}"?`}
                {confirmDialog.action === "process" &&
                  `Generate a temporary password for "${confirmDialog.displayText}"?`}
                {confirmDialog.action === "delete" && confirmDialog.type === "account" &&
                  `Are you sure you want to delete the account for "${confirmDialog.displayText}"? The player profile will be retained.`}
                {confirmDialog.action === "delete" && confirmDialog.type === "password" &&
                  `Are you sure you want to delete the password reset request for "${confirmDialog.displayText}"?`}
                {confirmDialog.action === "link" &&
                  `Select a new player profile to link the account for "${confirmDialog.displayText}" to:`}
              </Typography>

              {confirmDialog.action === "reject" && (
                <TextField
                  fullWidth
                  label="Rejection Reason (Optional)"
                  multiline
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  margin="normal"
                />
              )}

              {confirmDialog.action === "link" && (
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <InputLabel>Select Player Profile</InputLabel>
                  <Select
                    value={newLinkedPlayerId}
                    onChange={(e) => setNewLinkedPlayerId(e.target.value)}
                    label="Select Player Profile"
                  >
                    {allPlayers
                      .filter(
                        (p) =>
                          !p.hasAccount && p.id !== confirmDialog.requestId,
                      )
                      .map((player) => (
                        <MenuItem key={player.id} value={player.id}>
                          {player.name} ({player.division})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {tempPassword ? (
            <Button onClick={closeDialog} variant="contained">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={closeDialog} disabled={processing !== null}>
                Cancel
              </Button>
              <Button
                onClick={confirmAction}
                variant="contained"
                color={
                  confirmDialog.action === "reject" ||
                  confirmDialog.action === "delete"
                    ? "error"
                    : "primary"
                }
                disabled={processing !== null}
              >
                {processing !== null ? (
                  <CircularProgress size={24} />
                ) : (
                  "Confirm"
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Edit Restriction Dialog */}
      <Dialog
        open={editRestrictionDialog.open}
        onClose={() => !savingEdit && setEditRestrictionDialog({ open: false, player: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Restriction — {editRestrictionDialog.player?.name}</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mb: 2, mt: 1 }}>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>Restricted from:</FormLabel>
            <RadioGroup
              value={editBanType}
              onChange={(e) => setEditBanType(e.target.value as 'league-days' | 'tournaments' | 'both')}
            >
              <FormControlLabel value="league-days" control={<Radio />} label="League Days only" />
              <FormControlLabel value="tournaments" control={<Radio />} label="Tournaments only" />
              <FormControlLabel value="both" control={<Radio />} label="Both League Days and Tournaments" />
            </RadioGroup>
          </FormControl>
          <TextField
            label="Reason (optional)"
            multiline
            rows={3}
            fullWidth
            value={editBanReason}
            onChange={(e) => setEditBanReason(e.target.value)}
            placeholder="e.g., Violation of code of conduct"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditRestrictionDialog({ open: false, player: null })}
            disabled={savingEdit}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editRestrictionDialog.player) return;
              setSavingEdit(true);
              try {
                const token = localStorage.getItem('authToken');
                const response = await apiRequest(
                  `/api/players/${editRestrictionDialog.player.id}/ban`,
                  {
                    method: 'PUT',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ isBanned: true, banType: editBanType, bannedReason: editBanReason }),
                  }
                );
                if (response.ok) {
                  setSnackbar({ open: true, message: 'Restriction updated successfully', severity: 'success' });
                  setEditRestrictionDialog({ open: false, player: null });
                  fetchData();
                } else {
                  const err = await response.json();
                  setSnackbar({ open: true, message: err.error || 'Failed to update restriction', severity: 'error' });
                }
              } catch {
                setSnackbar({ open: true, message: 'Failed to update restriction', severity: 'error' });
              } finally {
                setSavingEdit(false);
              }
            }}
            disabled={savingEdit}
          >
            {savingEdit ? <CircularProgress size={22} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restrict/Unrestrict Dialog */}
      <Dialog 
        open={banDialog.open} 
        onClose={() => {
          setBanDialog({ open: false, player: null });
          setBanReason("");
          setBanType('both');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {banDialog.player?.isBanned ? "Remove Restriction" : "Restrict Player"}
        </DialogTitle>
        <DialogContent>
          <Alert severity={banDialog.player?.isBanned ? "info" : "warning"} sx={{ mb: 2 }}>
            {banDialog.player?.isBanned ? (
              <>
                Removing restriction for <strong>{banDialog.player?.name}</strong> will allow them to register for events again.
              </>
            ) : (
              <>
                Restricting <strong>{banDialog.player?.name}</strong> will block them from the selected areas.
                <Box sx={{ mt: 1 }}>
                  They will still be able to view their stats, match history, and manage their GDPR data.
                </Box>
              </>
            )}
          </Alert>

          {!banDialog.player?.isBanned && (
            <>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>Restrict from:</FormLabel>
                <RadioGroup
                  value={banType}
                  onChange={(e) => setBanType(e.target.value as 'league-days' | 'tournaments' | 'both')}
                >
                  <FormControlLabel value="league-days" control={<Radio />} label="League Days only (cannot register for league days or new seasons)" />
                  <FormControlLabel value="tournaments" control={<Radio />} label="Tournaments only (cannot register for tournaments)" />
                  <FormControlLabel value="both" control={<Radio />} label="Both League Days and Tournaments" />
                </RadioGroup>
              </FormControl>

              <TextField
                label="Reason for restriction (optional)"
                multiline
                rows={3}
                fullWidth
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder="e.g., Violation of code of conduct, inappropriate behavior, etc."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBanDialog({ open: false, player: null });
              setBanReason("");
              setBanType('both');
            }}
            disabled={processing === banDialog.player?.id}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBanConfirm}
            variant="contained"
            color={banDialog.player?.isBanned ? "success" : "error"}
            disabled={processing === banDialog.player?.id}
          >
            {processing === banDialog.player?.id ? (
              <CircularProgress size={24} />
            ) : banDialog.player?.isBanned ? (
              "Remove Restriction"
            ) : (
              "Restrict Player"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Temp Password Dialog */}
      <Dialog
        open={tempPasswordDialog.open}
        onClose={() => setTempPasswordDialog({ open: false, player: null, generatedPassword: null, loading: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generate Temporary Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Player: <strong>{tempPasswordDialog.player?.name}</strong> (@{tempPasswordDialog.player?.username})
          </Typography>
          {!tempPasswordDialog.generatedPassword ? (
            <Alert severity="info">
              This will generate a temporary password and require the player to change it on next login.
            </Alert>
          ) : (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Temporary password generated. Share it with the player securely.
              </Alert>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  backgroundColor: "action.hover",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: "1.1rem",
                  letterSpacing: 1,
                }}
              >
                <Typography sx={{ flex: 1, fontFamily: "monospace", fontWeight: "bold" }}>
                  {tempPasswordDialog.generatedPassword}
                </Typography>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(tempPasswordDialog.generatedPassword!);
                      setSnackbar({ open: true, message: "Copied to clipboard", severity: "success" });
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTempPasswordDialog({ open: false, player: null, generatedPassword: null, loading: false })}>
            Close
          </Button>
          {!tempPasswordDialog.generatedPassword && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleGenerateTempPassword}
              disabled={tempPasswordDialog.loading}
              startIcon={tempPasswordDialog.loading ? <CircularProgress size={16} /> : <KeyIcon />}
            >
              Generate
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default PlayerAccountRequests;
